import type { ReactNode } from "react"
import { Fragment, lazy, Suspense } from "react"
import { Navigate, Route, Routes, useParams } from "react-router-dom"

import { message } from "@/atoms"
import { NAMES_ROUTES, PARAMS } from "@/const/routes.constants"
import { isKnownUserRole, isLiveAiPoliciesEnabled } from "@/domain/user"
import { isSelfHosted } from "@/helpers/self-hosted.helper"
import { useServices } from "@/hooks/useServices"
import { useFeatureFlags, useUser } from "@/hooks/useUser"
import { ComingSoonPage } from "@/pages/ComingSoonPage"
import { AiPolicyProvider } from "./components/organisms/AiPolicyProvider/AiPolicyProvider"
import { LiveAiPolicyProvider } from "./components/organisms/LiveAiPolicyProvider"
import { AiPoliciesPage } from "./components/pages/AiPoliciesPage"
import {
  AI_POLICIES_TABS,
  DECISIONS_PATH
} from "./components/pages/AiPoliciesPage/const"
import { AiPolicyPage } from "./components/pages/AiPolicyPage"
import { AutomatedActionPage } from "./components/pages/AutomatedActionPage"
import { AutomatedActionsPage } from "./components/pages/AutomatedActionsPage"
import { LiveAiPolicyPage } from "./components/pages/LiveAiPolicyPage"
import { PoliciesPage } from "./components/pages/PoliciesPage"
import { PolicyPageFallback } from "./components/pages/PolicyPage/components/PolicyPageFallback"
import { ScanHistoryPage } from "./components/pages/ScanHistoryPage"

import type { PolicyType } from "@/api/usePolicies.types"

const PolicyPage = lazy(() => import("./components/pages/PolicyPage"))

// Backward compatibility: redirect old /ci/* routes to /ci-sca/*
const LegacyCIRedirect = () => {
  const { GOVERNANCE } = NAMES_ROUTES
  const params = useParams()
  const subType = params[PARAMS.SUB_TYPE] || "protect"
  const id = params[PARAMS.ID]
  const path = id
    ? `/${GOVERNANCE.ROOT}/${GOVERNANCE.CI_SCA}/${subType}/${id}`
    : `/${GOVERNANCE.ROOT}/${GOVERNANCE.CI_SCA}/${subType}`
  return <Navigate to={path} replace />
}

export const GovernancePage = () => {
  const { GOVERNANCE } = NAMES_ROUTES
  const { ROOT, WORKFLOWS, SCM, CI, CI_SCA, CI_SAST, AI } = GOVERNANCE
  const [messageApi, messageContext] = message.useMessage()
  const notSelfHosted = !isSelfHosted()
  const policies = [SCM, GOVERNANCE.CODE, CI_SCA, CI_SAST]
  const defaultPolicy = notSelfHosted ? SCM : GOVERNANCE.CODE
  const { auth } = useServices()
  const { aiDiscovery, aiPolicies } = useFeatureFlags()
  const isDemo = auth.isDemo()
  // Demo tenants keep the demo policies; live AI policies get the real ones,
  // on the same pages
  const isLiveAiPolicies =
    !isDemo && isLiveAiPoliciesEnabled(aiDiscovery, aiPolicies)
  // The flags come with the user's role: until then a deep link to the AI
  // policies waits instead of falling to the default policies
  const isFlagsPending = !isKnownUserRole(useUser().user)

  // The AI policies' pages, whichever provider serves the policies; a real
  // policy opens in its own page
  const aiPolicyRoutes = (policyPage: ReactNode) => (
    <>
      <Route
        path={`/${AI}`}
        element={<AiPoliciesPage tab={AI_POLICIES_TABS.POLICIES} />}
      />
      <Route
        path={`/${AI}/${DECISIONS_PATH}`}
        element={<AiPoliciesPage tab={AI_POLICIES_TABS.DECISIONS} />}
      />
      <Route path={`/${AI}/:${PARAMS.ID}/:mode`} element={policyPage} />
    </>
  )

  return (
    <>
      <Routes>
        <Route
          path={`/${GOVERNANCE.SCAN_HISTORY}`}
          element={<ScanHistoryPage />}
        />
        {notSelfHosted && (
          <>
            <Route
              path={`/${GOVERNANCE.THREAT_DETECTION_POLICIES}`}
              element={<ComingSoonPage keyPage="threatDetectionPolicies" />}
            />
            <Route
              path={`/${WORKFLOWS}`}
              element={
                <AutomatedActionsPage
                  messageApi={messageApi}
                  messageContext={messageContext}
                />
              }
            />
            <Route
              path={`/${WORKFLOWS}/:${PARAMS.ID}`}
              element={<AutomatedActionPage messageApi={messageApi} />}
            />
          </>
        )}

        {(policies as PolicyType[]).map((type) => (
          <Fragment key={type}>
            <Route
              path={`/${type}/:${PARAMS.SUB_TYPE}`}
              element={<PoliciesPage type={type} />}
            />
            <Route
              path={`/${type}/:${PARAMS.SUB_TYPE}/:${PARAMS.ID}`}
              element={
                <Suspense fallback={<PolicyPageFallback type={type} />}>
                  <PolicyPage type={type} />
                </Suspense>
              }
            />
            <Route
              path={`/${type}/*`}
              element={<Navigate to={`/${ROOT}/${type}/protect`} />}
            />
          </Fragment>
        ))}
        {isDemo && (
          <Route element={<AiPolicyProvider />}>
            {aiPolicyRoutes(<AiPolicyPage />)}
          </Route>
        )}
        {isLiveAiPolicies && (
          <Route element={<LiveAiPolicyProvider />}>
            {aiPolicyRoutes(<LiveAiPolicyPage />)}
          </Route>
        )}
        {!isDemo && !isLiveAiPolicies && isFlagsPending && (
          <Route path={`/${AI}/*`} element={null} />
        )}
        {/* Backward compatibility: redirect old /ci/* routes to /ci-sca/* */}
        <Route
          path={`/${CI}/:${PARAMS.SUB_TYPE}/:${PARAMS.ID}`}
          element={<LegacyCIRedirect />}
        />
        <Route
          path={`/${CI}/:${PARAMS.SUB_TYPE}`}
          element={<LegacyCIRedirect />}
        />
        <Route
          path={`/${CI}/*`}
          element={<Navigate to={`/${ROOT}/${CI_SCA}/protect`} replace />}
        />
        <Route
          path="/*"
          element={<Navigate to={`/${ROOT}/${defaultPolicy}`} />}
        />
      </Routes>
      {messageContext}
    </>
  )
}
